import { api } from "@/modules/api";
import { AppStore } from "@/store";
import { waitForLiveQuerySync } from "@/store/sync/utils";
import { runInAction } from "mobx";
import { logger } from "@/modules/logger";
import { toastModule } from "@/modules/toast";
import { objectModule } from "@/modules/object";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { truncateSuggestionDataForTracking } from "@/store/note/helpers/suggestionTracking";
import { Duration } from "luxon";

export const refineNote = async ({
  noteId,
  userPrompt,
  sessionId,
  store,
}: {
  noteId: string;
  userPrompt: string;
  sessionId: string;
  store: AppStore;
}) => {
  const startTime = Date.now();

  try {
    const note = await store.notes.getAsync(noteId);
    if (!note) return { success: false, error: "Note not found" };
    runInAction(() => (note.isProcessingSuggestion = true));

    try {
      const response = await api.post("/v2/notes/{note_id}/refine", {
        params: { path: { note_id: noteId } },
        body: {
          user_prompt: userPrompt,
          session_id: sessionId,
        },
      });

      if (response.response.ok) {
        await waitForLiveQuerySync({
          syncStore: store.sync,
          query: async () => {
            try {
              const spaceAccountNote = await store.spaceAccountNotes.localTable.get(
                note.spaceAccountNoteId
              );
              return !!spaceAccountNote?.model_data?.suggestion_data;
            } catch (_) {
              return false;
            }
          },
          timeout: Duration.fromObject({ seconds: 90 }),
        });

        const spaceAccountNote = await store.spaceAccountNotes.localTable.get(
          note.spaceAccountNoteId
        );
        const updatedSuggestionData = spaceAccountNote?.model_data?.suggestion_data;

        const endTime = Date.now();
        const elapsedTime = Math.floor((endTime - startTime) / 1000);

        const { previousMarkdownContents, suggestedMarkdownContents, userPrompt } =
          truncateSuggestionDataForTracking(updatedSuggestionData);

        trackEvent(TrackedEvent.NoteCleanupRefine, {
          session_id: sessionId,
          note_id: noteId,
          elapsed_time: elapsedTime,
          previous_title: updatedSuggestionData?.prev_primary_label,
          previous_markdown_contents: previousMarkdownContents,
          suggested_markdown_contents: suggestedMarkdownContents,
          suggested_title: updatedSuggestionData?.suggested_primary_label,
          user_prompt: userPrompt,
        });

        return { success: true };
      } else {
        throw new Error(response.error?.error_message || "Unknown API error");
      }
    } finally {
      runInAction(() => (note.isProcessingSuggestion = false));
    }
  } catch (e: unknown) {
    logger.error({
      message: "Failed to refine note",
      info: {
        error: objectModule.safeErrorAsJson(e as Error),
        noteId,
        userPrompt,
        sessionId,
        suggestionKind: "NOTE_CLEANUP_V1",
      },
    });
    toastModule.triggerToast({ content: "Failed to refine note. Please try again." });
    return { success: false, error: e instanceof Error ? e.message : "Unknown error" };
  }
};
